'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterPanelProps {
  isOpen?: boolean;
  onClose?: () => void;
  onApplyFilters?: (filters: FilterState) => void;
  className?: string;
}

interface FilterState {
  brands: string[];
  services: string[];
  priceRange: [number, number];
  distance: number;
  rating: number;
  certifications: string[];
  availability: string[];
}

const FilterPanel = ({
  isOpen = false,
  onClose,
  onApplyFilters,
  className = ''
}: FilterPanelProps) => {
  const [filters, setFilters] = useState<FilterState>({
    brands: [],
    services: [],
    priceRange: [0, 500],
    distance: 25,
    rating: 0,
    certifications: [],
    availability: []
  });

  const brands = ['BMW', 'Lexus', 'Mercedes-Benz', 'Audi', 'Acura', 'Infiniti', 'Toyota', 'Honda'];
  const services = [
    'Oil Change',
    'Brake Service',
    'Engine Diagnostics',
    'Transmission Repair',
    'Suspension Work',
    'Electrical Systems',
    'AC Repair',
    'Tire Service',
    'Body Shop'
  ];
  const certifications = ['ASE Certified', 'Acura Specialist', 'BMW Specialist', 'Lexus Specialist', 'Bosch Certified'];
  const availability = ['Open Now', 'Same Day Service', 'Weekend Hours', 'Emergency Service'];

  const handleBrandToggle = (brand: string) => {
    setFilters(prev => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter(b => b !== brand)
        : [...prev.brands, brand]
    }));
  };

  const handleServiceToggle = (service: string) => {
    setFilters(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleCertificationToggle = (cert: string) => {
    setFilters(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleAvailabilityToggle = (avail: string) => {
    setFilters(prev => ({
      ...prev,
      availability: prev.availability.includes(avail)
        ? prev.availability.filter(a => a !== avail)
        : [...prev.availability, avail]
    }));
  };

  const handleClearAll = () => {
    setFilters({
      brands: [],
      services: [],
      priceRange: [0, 500],
      distance: 25,
      rating: 0,
      certifications: [],
      availability: []
    });
  };

  const handleApply = () => {
    if (onApplyFilters) {
      onApplyFilters(filters);
    }
    if (onClose) {
      onClose();
    }
  };

  const activeFilterCount = 
    filters.brands.length + 
    filters.services.length + 
    filters.certifications.length + 
    filters.availability.length +
    (filters.rating > 0 ? 1 : 0);

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile Backdrop */}
      <div 
        className="lg:hidden fixed inset-0 z-[150] bg-background"
        onClick={onClose}
      />

      {/* Filter Panel */}
      <div className={`fixed lg:sticky top-16 lg:top-24 left-0 right-0 lg:left-auto lg:right-auto z-[150] lg:z-auto h-[calc(100vh-4rem)] lg:h-auto overflow-y-auto bg-card lg:rounded-lg lg:shadow-elevation-md ${className}`}>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="AdjustmentsHorizontalIcon" size={20} className="text-primary" />
              <h3 className="font-heading text-lg font-semibold text-text-primary">
                Filters
              </h3>
              {activeFilterCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="lg:hidden flex items-center justify-center rounded-md p-1 text-text-secondary transition-smooth hover:bg-muted hover:text-text-primary"
              aria-label="Close filters"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>

          {/* Vehicle Brands */}
          <div className="space-y-3">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Vehicle Brand
            </h4>
            <div className="space-y-2">
              {brands.map(brand => (
                <label key={brand} className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filters.brands.includes(brand)}
                    onChange={() => handleBrandToggle(brand)}
                    className="h-4 w-4 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  />
                  <span className="font-body text-sm text-text-secondary group-hover:text-text-primary transition-smooth">
                    {brand}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="space-y-3 border-t border-border pt-6">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Services Offered
            </h4>
            <div className="space-y-2">
              {services.map(service => (
                <label key={service} className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filters.services.includes(service)}
                    onChange={() => handleServiceToggle(service)}
                    className="h-4 w-4 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  />
                  <span className="font-body text-sm text-text-secondary group-hover:text-text-primary transition-smooth">
                    {service}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Distance */}
          <div className="space-y-3 border-t border-border pt-6">
            <div className="flex items-center justify-between">
              <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
                Distance
              </h4>
              <span className="font-mono text-sm font-medium text-primary">
                {filters.distance} mi
              </span>
            </div>
            <input
              type="range"
              min="5"
              max="50"
              step="5"
              value={filters.distance}
              onChange={(e) => setFilters(prev => ({ ...prev, distance: parseInt(e.target.value) }))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between font-caption text-xs text-muted-foreground">
              <span>5 mi</span>
              <span>50 mi</span>
            </div>
          </div>

          {/* Rating */}
          <div className="space-y-3 border-t border-border pt-6">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Minimum Rating
            </h4>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  onClick={() => setFilters(prev => ({ ...prev, rating: rating === prev.rating ? 0 : rating }))}
                  className="transition-smooth hover:scale-110 active:scale-95"
                  aria-label={`${rating} stars`}
                >
                  <Icon
                    name="StarIcon"
                    size={24}
                    variant={rating <= filters.rating ? 'solid' : 'outline'}
                    className={rating <= filters.rating ? 'text-accent' : 'text-muted-foreground'}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="space-y-3 border-t border-border pt-6">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Certifications
            </h4>
            <div className="space-y-2">
              {certifications.map(cert => (
                <label key={cert} className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filters.certifications.includes(cert)}
                    onChange={() => handleCertificationToggle(cert)}
                    className="h-4 w-4 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  />
                  <span className="font-body text-sm text-text-secondary group-hover:text-text-primary transition-smooth">
                    {cert}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Availability */}
          <div className="space-y-3 border-t border-border pt-6">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Availability
            </h4>
            <div className="space-y-2">
              {availability.map(avail => (
                <label key={avail} className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filters.availability.includes(avail)}
                    onChange={() => handleAvailabilityToggle(avail)}
                    className="h-4 w-4 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  />
                  <span className="font-body text-sm text-text-secondary group-hover:text-text-primary transition-smooth">
                    {avail}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-card border-t border-border pt-6 space-y-3">
            <button
              onClick={handleApply}
              className="w-full flex items-center justify-center space-x-2 rounded-lg bg-primary px-6 py-3 font-body text-sm font-medium text-primary-foreground shadow-elevation-sm transition-smooth hover:opacity-90 active:scale-[0.97]"
            >
              <Icon name="CheckIcon" size={18} />
              <span>Apply Filters</span>
            </button>
            <button
              onClick={handleClearAll}
              className="w-full rounded-lg border border-border px-6 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97]"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;